import { useEffect } from 'react';

/**
 * Keeps the screen awake for the lifetime of the calling component (or while
 * `active` is true) via the Screen Wake Lock API. Used during a ritual session
 * so the phone doesn't dim/lock mid-practice when the user isn't touching it.
 *
 * Robustness notes:
 * - The OS auto-releases the lock whenever the tab becomes hidden (app switch,
 *   notification shade, lock). We re-acquire it on `visibilitychange` when the
 *   document is visible again and we're still `active`.
 * - Everything is guarded and wrapped in try/catch: unsupported browsers and
 *   rejected requests (e.g. low battery) silently no-op — no UI, no throw.
 */
export function useWakeLock(active = true): void {
  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const request = async () => {
      if (released) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        /* ignore — unsupported, denied, or low battery */
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void request();
    };

    void request();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      sentinel?.release().catch(() => {
        /* ignore */
      });
      sentinel = null;
    };
  }, [active]);
}
